import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useFormContext, useFieldArray, UseFieldArrayRemove } from 'react-hook-form';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';

import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteIcon from '@mui/icons-material/Delete';

import { MultipleChoiceExerciseInput, MultipleChoiceOptionInput, MultipleChoiceOptionType } from '../../types/form';
import { FormTextField } from '@ts-core/core/components/FormV2';

import MultipleChoiceQuestionOptionField from '../MultipleChoiceQuestionOptionField';

interface MultipleChoiceQuestionFieldProps {
  index: number;

  getNextOption: (index: number) => MultipleChoiceOptionInput;
  onRemove: UseFieldArrayRemove;
  canAddMoreOptions: (count: number) => boolean;

  allOptionTypes: MultipleChoiceOptionType[];
  defaultOptionType: MultipleChoiceOptionType['value'];
}

const MultipleChoiceQuestionField = (props: MultipleChoiceQuestionFieldProps) => {
  const { index, onRemove, getNextOption, allOptionTypes, defaultOptionType, canAddMoreOptions } = props;
  const intl = useIntl();
  const { control } = useFormContext<MultipleChoiceExerciseInput>(); // retrieve all hook methods
  const { fields, append, remove } = useFieldArray({ control, name: `questions.${index}.options` });

  const handleRemoveQuestion = useCallback(() => {
    onRemove(index);
  }, [onRemove, index]);

  const handleAddOption = useCallback(() => {
    append(getNextOption(fields.length));
  }, [append, fields, getNextOption]);

  return (
    <Box width="100%">
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <FormTextField
          control={control}
          name={`questions.${index}.text`}
          fullWidth
          label={intl.formatMessage({ id: 'field.question' })}
          rules={{ required: 'El texto de la pregunta es requerido' }}
        />
        <Box width={32} height={32} display="flex" alignItems="center">
          {index > 0 && (
            <IconButton onClick={handleRemoveQuestion} size="small">
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </Stack>
      <Stack mt={2} direction="column" spacing={1}>
        {fields.map((field, optionIndex) => (
          <MultipleChoiceQuestionOptionField
            key={field.id}
            index={optionIndex}
            questionIndex={index}
            onRemove={remove}
            allOptionTypes={allOptionTypes}
            defaultOptionType={defaultOptionType}
          />
        ))}
      </Stack>
      {canAddMoreOptions(fields.length) && (
        <Button
          onClick={handleAddOption}
          sx={{
            mt: 1,
            color: 'grey.400',
            // borderBottom: '1px solid',
            borderRadius: 0,
            transition: 'color 0.2s ease-in-out',
            '&:hover': {
              color: 'black',
              backgroundColor: 'unset',
            },
          }}
        >
          <RadioButtonUncheckedIcon sx={{ ml: -0.5, mr: 1 }} />
          {intl.formatMessage({ id: 'button.addOption' })}
        </Button>
      )}
    </Box>
  );
};

export default MultipleChoiceQuestionField;
